import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Grid,
  Typography,
  Card,
  CardContent,
  Button,
  CircularProgress,
  Alert,
  Paper,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material';


// Import our component styles hook
import useComponentStyles from '../../theme/useComponentStyles';

// Import phosphor icons for consistency
import { 
  Package, 
  Truck, 
  CheckCircle,
  Clock,
  ArrowLeft,
  Eye
} from '@phosphor-icons/react';

function Orders() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [orders, setOrders] = useState([]);
  const styles = useComponentStyles();

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        
        // Mock orders data - replace with actual API call
        const mockOrders = [
          {
            id: 'ORD-001',
            type: 'Invitations',
            status: 'Shipped',
            orderDate: '2024-01-15',
            deliveryDate: '2024-01-20',
            total: 89.99,
            items: 50
          },
          {
            id: 'ORD-002',
            type: 'Wedding Favors',
            status: 'Processing',
            orderDate: '2024-01-10',
            deliveryDate: '2024-01-25',
            total: 156.50,
            items: 100
          },
          {
            id: 'ORD-003',
            type: 'Place Cards',
            status: 'Delivered',
            orderDate: '2024-01-05',
            deliveryDate: '2024-01-12',
            total: 45.00,
            items: 75
          }
        ];
        
        setOrders(mockOrders);
      } catch (error) {
        setError('Failed to fetch orders');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const getStatusColor = (status) => {
    switch (status) {
      case 'Delivered':
        return 'success';
      case 'Shipped':
        return 'primary';
      case 'Processing':
        return 'warning';
      case 'Cancelled':
        return 'error';
      default:
        return 'default';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'Delivered':
        return <CheckCircle size={16} weight="fill" />;
      case 'Shipped':
        return <Truck size={16} weight="fill" />;
      case 'Processing':
        return <Clock size={16} weight="fill" />;
      default:
        return <Package size={16} weight="fill" />;
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={styles.containers.pageContainer}>
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      <Grid container spacing={3}>
        <Grid item xs={12}>
          <Box sx={styles.layout.flexRow} gap={2} alignItems="center" mb={2}>
            <Button
              startIcon={<ArrowLeft />}
              onClick={() => navigate('/stationary-gifts')}
              sx={{ minWidth: 'auto' }}
            >
              Back
            </Button>
            <Typography component="h2" className="pagename" sx={styles.text.pagename}>
              My Orders
            </Typography>
          </Box>
          <Typography variant="body1" color="text.secondary" paragraph sx={styles.text.sectionDescription}>
            Track and monitor your stationary and gift purchases.
          </Typography>
        </Grid>

        {/* Orders summary card */}
        <Grid item xs={12}>
          <Card 
            elevation={0} 
            sx={styles.cards.dashboardSection}
          >
            <CardContent sx={{ p: 3 }}>
              <Grid container spacing={2} alignItems="center">
                <Grid item xs={12} md={8}>
                  <Typography variant="h5" gutterBottom sx={styles.text.cardTitle}>
                    Order Summary
                  </Typography>
                  <Box sx={{ ...styles.layout.flexColumn, gap: 1 }}>
                    <Box sx={styles.layout.flexCenter} style={{ justifyContent: 'flex-start' }}>
                      <Package size={20} weight="regular" style={{ marginRight: 8 }} />
                      <Typography variant="body1">
                        {orders.length} Total Orders
                      </Typography>
                    </Box>
                    <Box sx={styles.layout.flexCenter} style={{ justifyContent: 'flex-start' }}>
                      <Truck size={20} weight="regular" style={{ marginRight: 8 }} />
                      <Typography variant="body1">
                        {orders.filter(o => o.status === 'Shipped' || o.status === 'Delivered').length} Shipped
                      </Typography>
                    </Box>
                  </Box>
                </Grid>
                <Grid item xs={12} md={4}>
                  <Box sx={{ ...styles.layout.flexColumn, gap: 1, alignItems: 'flex-end' }}>
                    <Chip 
                      label="Orders" 
                      color="primary" 
                      variant="outlined"
                      size="small"
                    />
                  </Box>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        {/* Orders table */}
        <Grid item xs={12}>
          <Card sx={styles.cards.default}>
            <CardContent>
              <Typography variant="h6" sx={styles.text.sectionTitle} mb={3}>
                Recent Orders
              </Typography>
              
              {orders.length > 0 ? (
                <TableContainer component={Paper} elevation={0}>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>Order ID</TableCell>
                        <TableCell>Type</TableCell>
                        <TableCell>Status</TableCell>
                        <TableCell>Order Date</TableCell>
                        <TableCell>Delivery Date</TableCell>
                        <TableCell>Items</TableCell>
                        <TableCell>Total</TableCell>
                        <TableCell>Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {orders.map((order) => (
                        <TableRow key={order.id} hover>
                          <TableCell>
                            <Typography variant="body2" fontWeight="medium">
                              {order.id}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2">
                              {order.type}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Chip
                              icon={getStatusIcon(order.status)}
                              label={order.status}
                              color={getStatusColor(order.status)}
                              size="small"
                              variant="outlined"
                            />
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2">
                              {new Date(order.orderDate).toLocaleDateString()}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2">
                              {new Date(order.deliveryDate).toLocaleDateString()}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2">
                              {order.items}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" fontWeight="medium">
                              ${order.total}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Button
                              size="small"
                              startIcon={<Eye size={16} />}
                              onClick={() => navigate(`/stationary-gifts/orders/${order.id}`)}
                            >
                              View
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              ) : (
                <Box sx={{ textAlign: 'center', py: 4 }}>
                  <Package size={48} weight="regular" color="text.secondary" />
                  <Typography variant="h6" color="text.secondary" mt={2}>
                    No Orders Yet
                  </Typography>
                  <Typography variant="body2" color="text.secondary" mt={1}>
                    Your orders will appear here once you make purchases.
                  </Typography>
                  <Button
                    variant="contained"
                    onClick={() => navigate('/stationary-gifts')}
                    sx={{ mt: 2 }}
                  >
                    Browse Stationary & Gifts
                  </Button>
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
}

export default Orders; 